import config from '../../utils/config';

export interface AuthGuardUser {
  email: string;
  firstName?: string;
  lastName?: string;
  profileName?: string;
  name?: string; // Computed from first + last name
}

export interface UserAccess {
  access_id: number;
  user_email: string;
  user_name?: string;
  client_id: number;
  client_name?: string;
  client_number?: string;
  granted_by_email: string;
  granted_by_name?: string;
  granted_at: string;
  revoked_at?: string;
  is_active: boolean;
  notes?: string;
}

export interface ClientUsersResponse {
  success: boolean;
  client_id: number;
  users: UserAccess[];
  count: number;
}

export interface ClientUserEmailsResponse {
  success: boolean;
  client_id: number;
  user_emails: string[];
  count: number;
}

export interface GrantAccessResponse {
  success: boolean;
  access?: UserAccess;
  granted_count?: number;
  message: string;
}

export interface RevokeAccessResponse {
  success: boolean;
  revoked_count?: number;
  message: string;
}

class UserClientAccessService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = config.api.snowflake;
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('authguard_access_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  /**
   * Get all users who have access to a specific client
   */
  async getClientUsers(clientId: number, activeOnly: boolean = true): Promise<ClientUsersResponse> {
    const response = await fetch(
      `${this.baseUrl}/api/v1/client-access/client/${clientId}/users?active_only=${activeOnly}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch client users: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get email addresses of users who have access to a specific client
   */
  async getClientUserEmails(clientId: number, activeOnly: boolean = true): Promise<ClientUserEmailsResponse> {
    const response = await fetch(
      `${this.baseUrl}/api/v1/client-access/client/${clientId}/user-emails?active_only=${activeOnly}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch client user emails: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Grant a single user access to a client using email address
   */
  async grantAccess(
    userEmail: string,
    clientId: number,
    userName?: string,
    notes?: string
  ): Promise<GrantAccessResponse> {
    const response = await fetch(`${this.baseUrl}/api/v1/client-access/grant`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        user_email: userEmail,
        user_name: userName,
        client_id: clientId,
        notes,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to grant access: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Search AuthGuard users by name or email
   */
  async searchUsers(searchTerm: string): Promise<AuthGuardUser[]> {
    if (!searchTerm || searchTerm.length < 2) {
      return [];
    }

    const response = await fetch(
      `${this.baseUrl}/api/v1/auth/users/search?searchTerm=${encodeURIComponent(searchTerm)}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to search users: ${response.statusText}`);
    }

    const data = await response.json();
    return data.users || [];
  }

  /**
   * Grant multiple users access to a client using email addresses
   */
  async grantBulkAccess(
    userEmails: string[],
    clientId: number,
    notes?: string
  ): Promise<GrantAccessResponse> {
    const response = await fetch(`${this.baseUrl}/api/v1/client-access/grant-bulk`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        user_emails: userEmails,
        client_id: clientId,
        notes,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to grant bulk access: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Revoke a single user's access to a client using email address
   */
  async revokeAccess(
    userEmail: string,
    clientId: number,
    notes?: string
  ): Promise<RevokeAccessResponse> {
    const response = await fetch(`${this.baseUrl}/api/v1/client-access/revoke`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        user_email: userEmail,
        client_id: clientId,
        notes,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to revoke access: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Revoke multiple users' access to a client using email addresses
   */
  async revokeBulkAccess(
    userEmails: string[],
    clientId: number,
    notes?: string
  ): Promise<RevokeAccessResponse> {
    const response = await fetch(`${this.baseUrl}/api/v1/client-access/revoke-bulk`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        user_emails: userEmails,
        client_id: clientId,
        notes,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to revoke bulk access: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get all clients a user has access to (by email)
   */
  async getUserClients(userEmail: string, activeOnly: boolean = true): Promise<{ success: boolean; user_email: string; clients: UserAccess[]; count: number }> {
    const response = await fetch(
      `${this.baseUrl}/api/v1/client-access/user/${encodeURIComponent(userEmail)}/clients?active_only=${activeOnly}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch user clients: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Check if current user has access to a specific client
   */
  async checkMyAccess(clientId: number): Promise<boolean> {
    const response = await fetch(
      `${this.baseUrl}/api/v1/client-access/check-access/me/${clientId}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to check access: ${response.statusText}`);
    }

    const data = await response.json();
    return data.has_access;
  }

  /**
   * Check if a user (by email) has access to a specific client
   */
  async checkAccess(userEmail: string, clientId: number): Promise<boolean> {
    const response = await fetch(
      `${this.baseUrl}/api/v1/client-access/check-access/${encodeURIComponent(userEmail)}/${clientId}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to check access: ${response.statusText}`);
    }

    const data = await response.json();
    return data.has_access;
  }
}

export default new UserClientAccessService();
